import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { catchError, EMPTY, Observable, switchMap } from 'rxjs';
import { actionTrackerContext } from '../../../core/interceptors/api-interceptor.interceptor';
import { ActionTrackerService } from '../../../core/services/action-tracker.service';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IDashboard } from '../../settings/dashboards/dashboards.interface';
import { IBoardItemConfiguration } from './widget-add-edit-dialog.interface';

export interface IAddEditDialogState {
  boards: IDropdownOption<number>[];
  boardsLoadStatus: ELoadStatus;
  errorStatus: number | null;
  itemCategories: IDropdownOption<number>[];
  itemCategoriesLoadStatus: ELoadStatus;
  subDashboards: IDropdownOption[];
  subDashboardsLoadStatus: ELoadStatus;
}

@Injectable()
export class AddEditDialogStore extends ComponentStore<IAddEditDialogState> {
  readonly boardsDropdown$: Observable<IDropdownOption<number>[]> = this.select((state: IAddEditDialogState) =>
    state.boards.map((b: IDropdownOption<number>) => ({ id: b.id, name: b.name })),
  );

  readonly itemCategoriesDropdown$: Observable<IDropdownOption<number>[]> = this.select((state: IAddEditDialogState) =>
    state.itemCategories.map((c: IDropdownOption<number>) => ({ id: c.id, name: c.name })),
  );

  private readonly subDashboardsSelect = this.selectSignal(({ subDashboards }) => subDashboards);

  readonly subDashboards = computed(() => sortBy(this.subDashboardsSelect(), 'name'));

  readonly loadBoards = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({
          boardsLoadStatus: ELoadStatus.loading,
          errorStatus: null,
        });

        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getBoards(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IDropdownOption<number>>) => {
              this.patchState({
                boards: response.data,
                boardsLoadStatus: ELoadStatus.success,
                errorStatus: null,
              });
            },
            (response: { status: number }) => {
              this.patchState({
                boardsLoadStatus: ELoadStatus.failure,
                errorStatus: response.status,
              });
            },
          ),
          catchError((response: { status: number }) => {
            this.patchState({
              boardsLoadStatus: ELoadStatus.failure,
              errorStatus: response.status,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  readonly loadItemCategories = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({
          errorStatus: null,
          itemCategoriesLoadStatus: ELoadStatus.loading,
        });

        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getItemCategories(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IBoardItemConfiguration>) => {
              this.patchState({
                errorStatus: null,
                itemCategories: ActionTrackerService.buildCategoryDropdownOptions(response.data, this.translate),
                itemCategoriesLoadStatus: ELoadStatus.success,
              });
            },
            (response: { status: number }) => {
              this.patchState({
                errorStatus: response.status,
                itemCategoriesLoadStatus: ELoadStatus.failure,
              });
            },
          ),
          catchError((response: { status: number }) => {
            this.patchState({
              errorStatus: response.status,
              itemCategoriesLoadStatus: ELoadStatus.failure,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  readonly loadSubDashboards = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({
          errorStatus: null,
          subDashboardsLoadStatus: ELoadStatus.loading,
        });

        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getSubDashboards(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IDashboard>) => {
              this.patchState({
                errorStatus: null,
                subDashboards: response.data,
                subDashboardsLoadStatus: ELoadStatus.success,
              });
            },
            (response: { status: number }) => {
              this.patchState({
                errorStatus: response.status,
                subDashboardsLoadStatus: ELoadStatus.failure,
              });
            },
          ),
          catchError((response: { status: number }) => {
            this.patchState({
              errorStatus: response.status,
              subDashboardsLoadStatus: ELoadStatus.failure,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly translate: TranslateService,
  ) {
    super({
      boards: [],
      boardsLoadStatus: ELoadStatus.initial,
      errorStatus: null,
      itemCategories: [],
      itemCategoriesLoadStatus: ELoadStatus.initial,
      subDashboards: [],
      subDashboardsLoadStatus: ELoadStatus.initial,
    });
  }

  private getBoards(params: HttpParams): Observable<IGetManyResponse<IDropdownOption<number>>> {
    return this.http.get<IGetManyResponse<IDropdownOption<number>>>('boards', {
      context: actionTrackerContext,
      params,
    });
  }

  private getItemCategories(params: HttpParams): Observable<IGetManyResponse<IBoardItemConfiguration>> {
    return this.http.get<IGetManyResponse<IBoardItemConfiguration>>('board-item-configurations', {
      context: actionTrackerContext,
      params,
    });
  }

  private getSubDashboards(params: HttpParams): Observable<IGetManyResponse<IDashboard>> {
    return this.http.get<IGetManyResponse<IDashboard>>('dashboards', {
      params,
    });
  }
}
